import React, { useEffect, useState } from "react";
import {
  Box,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import LoaderNew from "../Loader/LoaderNew";

const formatQtyFull = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const DispatchProgressTooltip = ({ row, children }) => {
  if (!row) return children;

  const lacking = Number(row.lacking_qty) || 0;
  const onTrack = row.on_track;

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
            {row.customer_name}
          </Typography>
          <Typography variant="caption" display="block">
            Schedule: {row.start_date} to {row.end_date} ({row.total_days} days)
          </Typography>
          <Typography variant="caption" display="block">
            Daily target: {formatQtyFull(row.daily_target_qty)} / day
          </Typography>
          <Typography variant="caption" display="block">
            Target qty: {formatQtyFull(row.target_qty)}
          </Typography>
          <Typography variant="caption" display="block">
            Expected by today ({row.days_elapsed} day(s)):{" "}
            {formatQtyFull(row.expected_qty_by_today)}
          </Typography>
          <Typography variant="caption" display="block">
            Achieved qty: {formatQtyFull(row.achieved_qty ?? row.shipped_qty)}
          </Typography>
          {lacking > 0 ? (
            <Typography variant="caption" display="block" sx={{ color: "#ffcdd2" }}>
              Lacking: {formatQtyFull(lacking)}
            </Typography>
          ) : (
            <Typography variant="caption" display="block" sx={{ color: "#c8e6c9" }}>
              On track — no lacking quantity
            </Typography>
          )}
          <Typography variant="caption" display="block">
            Status: {onTrack ? "On track" : "Behind schedule"}
          </Typography>
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};

const DispatchScheduleDashboardCharts = () => {
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [topPending, setTopPending] = useState([]);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!company_id) return;
      try {
        setLoading(true);
        const res = await configServ.getDispatchScheduleDashboardSummary({
          company_id,
        });
        if (res.status === 200 && res.data) {
          setCustomers(res.data.customers || []);
          setTopPending(res.data.top_pending || []);
        } else {
          setCustomers([]);
          setTopPending([]);
        }
      } catch (e) {
        console.log(e);
        setCustomers([]);
        setTopPending([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [company_id]);

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <LoaderNew />
        </Box>
      ) : customers.length === 0 ? (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography color="text.secondary" align="center">
            No dispatch schedules yet. Create schedules on the Dispatch Schedule
            page to see dispatch progress.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 2, width: "100%" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
            Scheduled vs Dispatch
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Daily invoice progress: target qty divided over schedule days vs
            invoiced qty achieved. Hover for target, expected, achieved, and
            lacking quantities.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {customers.map((row) => {
              const pct = row.completion_percent_display ?? row.completion_percent ?? 0;
              const barValue = Math.min(100, Math.max(0, pct));
              const barColor = row.on_track ? "#2e7d32" : "#c62828";

              return (
                <Box key={row.customer_id}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="baseline"
                    mb={0.5}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.customer_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pct}%
                    </Typography>
                  </Box>
                  <DispatchProgressTooltip row={row}>
                    <LinearProgress
                      variant="determinate"
                      value={barValue}
                      sx={{
                        height: 12,
                        borderRadius: 1,
                        bgcolor: "grey.200",
                        cursor: "pointer",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 1,
                          bgcolor: barColor,
                        },
                      }}
                    />
                  </DispatchProgressTooltip>
                </Box>
              );
            })}
          </Box>

          {topPending.filter((r) => Number(r.lacking_qty) > 0).length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Top pending customers (behind daily schedule)
              </Typography>
              {topPending
                .filter((row) => Number(row.lacking_qty) > 0)
                .map((row) => (
                  <Box
                    key={`pending-${row.customer_id}`}
                    display="flex"
                    justifyContent="space-between"
                    py={0.5}
                  >
                    <Typography variant="body2">{row.customer_name}</Typography>
                    <Typography variant="body2" color="error.main">
                      Lacking {formatQtyFull(row.lacking_qty)}
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default DispatchScheduleDashboardCharts;
